import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, switchMap, tap } from 'rxjs';

import { PlsInformationSection, PriceInformation, Vehicle } from 'src/app/api/owcc/models';
import { appErrorMinorLoading } from 'src/app/core/app-error/store/app-error.actions';
import { startLoadVehicle } from 'src/app/features/configuration/store/configuration.actions';
import { updateB2bPriceVisibilityOnSummaryPage } from 'src/app/store/common/common.actions';
import { selectConfigurationVehicle } from 'src/app/store/common/common.selectors';
import { selectVehicleId } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';

import { PriceDisplayData, PriceDisplayParams, PriceDisplayStatus, ProductData } from './price-display.interface';

interface FootnoteReferenceGroup {
  type: string;
  items: number[];
}

interface PriceDisplayState {
  data?: PriceDisplayData;
  productData?: PriceDisplayData;
  params?: PriceDisplayParams;
  footnoteReferences: FootnoteReferenceGroup[];
  plsStatus?: PriceDisplayStatus | null;
}

const defaultPriceDisplayState: PriceDisplayState = {
  data: undefined,
  productData: undefined,
  params: undefined,
  footnoteReferences: [],
  plsStatus: null
};

// eslint-disable-next-line complexity
const buildForcedVehicle = (params: PriceDisplayParams): Vehicle => ({
  vehicleId: params?.force?.vehicleId,
  plsInformationSection: {
    technicalData: params?.force?.technicalData
  } as PlsInformationSection,
  priceInformation: {
    basePrice: {
      netPrice: params?.force?.price?.basePrice?.netPrice
    },
    totalOptionPrice: {
      netPrice: params?.force?.price?.totalOptionPrice?.netPrice
    },
    configurationPrice: {
      netPrice: params?.force?.price?.configurationPrice?.netPrice
    },
    discountedPrice: {
      netPrice: params?.force?.price?.discountedPrice?.netPrice
    }
  } as PriceInformation
});

// eslint-disable-next-line complexity
const buildProductData = (vehicle: Vehicle | undefined): ProductData => ({
  vehicleId: vehicle?.vehicleId,
  productGroup: vehicle?.productGroup,
  basePriceAmount: vehicle?.priceInformation?.basePrice?.netPrice || undefined,
  totalOptionPriceAmount: vehicle?.priceInformation?.totalOptionPrice?.netPrice || undefined,
  configurationPriceAmount: vehicle?.priceInformation?.configurationPrice?.netPrice || undefined,
  discountablePriceAmount: vehicle?.priceInformation?.discountedPrice?.netPrice || undefined,
  technicalData: vehicle?.plsInformationSection?.technicalData || undefined
});
@Injectable()
export class PriceDisplayStore extends ComponentStore<PriceDisplayState> {
  public readonly data$ = this.select(state => ({
    data: state.data,
    params: state.params
  }));

  public readonly params$ = this.select(state => state?.params).pipe(filter(Boolean));

  public readonly selectVehicleId$ = this.select(state => state.params?.force?.vehicleId).pipe(
    switchMap(forcedVehicleId => (forcedVehicleId ? of(forcedVehicleId) : this.store.select(selectVehicleId)))
  );

  public readonly productData$: Observable<ProductData> = this.params$.pipe(filterTruthy()).pipe(
    switchMap(params =>
      params?.force
        ? of(buildForcedVehicle(params))
        : this.store.select(selectConfigurationVehicle).pipe(filterTruthy())
    ),
    map((vehicle): ProductData => this.checkPlsInformation(vehicle)),
    filter(productData => !!productData.technicalData),
    // to prevent too frequent updates on PLS, we only filter when the relevant data changes
    // relevant data is so far only the configurationPriceAmount and the co2EmissionTotalCombined
    // co2EmissionTotalCombined can affect the NoVA discount on the Austrian market even if the price itself does not change.
    distinctUntilChanged(
      (oldValue, newValue) =>
        oldValue.configurationPriceAmount === newValue.configurationPriceAmount &&
        oldValue.technicalData?.co2EmissionTotalCombined === newValue.technicalData?.co2EmissionTotalCombined
    )
  );

  // see https://pages.git.i.mercedes-benz.com/dh-io-pls/dcps-product-pricing/additional-documentation/configuration.html
  public readonly product$ = combineLatest([this.selectVehicleId$, this.productData$]).pipe(
    filter(([vehicleId, productData]) => vehicleId === productData.vehicleId),
    map(([, productData]) => ({
      ...productData,
      offeringLocation: undefined // a CEASAR ID (intentionally static / undefined)
    }))
  );

  public readonly priceDisplayStatus$ = this.select(state => state.plsStatus);

  public readonly updateParams = this.effect((params$: Observable<PriceDisplayParams>) =>
    params$.pipe(
      filterTruthy(),
      tap((params: PriceDisplayParams) => {
        this.patchState({
          params
        });
      })
    )
  );

  private tryReloadVehicleData = true;

  /**
   * Updates the (global) store about the b2b price visibility (since this has an effect on the footnotes).
   * 'success' => set to true, everything else (especially 'unmount') => set to false
   */
  private readonly updateStoreAboutB2bPriceVisibility = this.effect(trigger$ =>
    trigger$.pipe(
      switchMap(() => this.priceDisplayStatus$),
      tap(priceDisplayStatus => {
        this.store.dispatch(
          updateB2bPriceVisibilityOnSummaryPage({
            isB2bPriceOnSummaryPageVisible: priceDisplayStatus === PriceDisplayStatus.Success
          })
        );
      })
    )
  );

  public constructor(private readonly store: Store) {
    super(defaultPriceDisplayState);
    this.updateStoreAboutB2bPriceVisibility();
  }

  public readonly updatePlsStatus = (plsStatus: PriceDisplayStatus): void => {
    this.patchState({ plsStatus });
  };

  private checkPlsInformation(vehicle: Vehicle | null): ProductData {
    // if vehicle (e.g. motorizationVehicle does not contain essential plsInformation, then load full Vehicle)
    if (!vehicle?.plsInformationSection && vehicle?.vehicleId) {
      // To avoid infite loop, we try to call full vehicle only once
      if (this.tryReloadVehicleData) {
        this.store.dispatch(startLoadVehicle({ vehicleId: vehicle.vehicleId }));
      } else {
        this.store.dispatch(appErrorMinorLoading({ error: {} } as HttpErrorResponse));
      }

      this.tryReloadVehicleData = !this.tryReloadVehicleData;

      return { vehicleId: vehicle.vehicleId } as ProductData;
    }

    return buildProductData(vehicle ?? undefined);
  }
}
